import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import NavBar from "../../components/common/NavBar";
import RealtimeAreaMap, { AreaPolygon } from "../../components/common/RealtimeAreaMap";
import { useThemeColors } from "../../hooks/useThemeColors";
import type { ThemeColors } from "../../config/theme";
import EeActivityDashboard from "./EeActivityDashboard";
import CatalogueActivityDashboard from "../activities/CatalogueActivityDashboard";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface Row {
  geo_id: any;
  geo_name?: string;
  time_bucket: any; // hour ("0".."23") for hourly, or "YYYY-MM-DD" for daily
  date?: string; // calendar day on hourly-series rows
  sum_unique_people: number; // people staying (presence), population-scaled
  home_share?: number; // residential vs workplace, exact ratio
  unique_people?: number; // distinct devices with a stay in the cell (observed)
  unique_events?: number; // total stays/events in the cell (observed)
  population?: number; // census residents in the cell (placement prior)
  employment?: number; // census jobs in the cell (placement prior)
}

type Measure = "unique_people" | "unique_events";
type ViewMode = "average" | "series";

const bucket = (t: any) => (typeof t === "object" && t?.value != null ? t.value : t);
const isDateStr = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);
const fmtDate = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });

// Light → dark green ramp for the heat map: the brand colour at rising opacity, so it reads on
// both the light and the dark basemap.
const heatColor = (colors: ThemeColors, t: number) => colors.css("brand-700", 0.15 + 0.85 * Math.max(0, Math.min(1, t)));

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-surface rounded-lg shadow p-4">
    <div className="text-xs text-ink-subtle">{label}</div>
    <div className="text-2xl font-semibold text-ink">{value}</div>
  </div>
);

const Card: React.FC<{ title: string; subtitle?: string; right?: React.ReactNode; children: React.ReactNode }> = ({ title, subtitle, right, children }) => (
  <div className="bg-surface rounded-lg shadow p-4">
    <div className="flex items-start justify-between gap-3">
      <div>
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        {subtitle && <p className="text-xs text-ink-subtle mb-3">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

const TartuActivityDashboard: React.FC = () => {
  const colors = useThemeColors();
  const location = useLocation();
  const state = (location.state as any) || {};
  const results: Row[] = useMemo(() => (state.jobResults as Row[]) || [], [state.jobResults]);
  const selectedGrids: any[] = useMemo(() => state.selectedGrids || [], [state.selectedGrids]);
  const measure: Measure = state.measure === "unique_events" ? "unique_events" : "unique_people";
  const measureLabel = measure === "unique_events" ? "Unique events" : "Unique people";
  const [viewMode, setViewMode] = useState<ViewMode>("average");

  const stats = useMemo(() => {
    // ── Per-cell aggregates (date-independent): heat map + top areas ──────────
    const cell = new Map<string, { value: number; home_share?: number; name: string; pop?: number; emp?: number; presSum: number; presN: number }>();
    for (const r of results) {
      const k = String(r.geo_id);
      const measureVal = Number((measure === "unique_events" ? r.unique_events : r.unique_people) ?? 0);
      const cur = cell.get(k) || { value: 0, home_share: undefined as number | undefined, name: r.geo_name || k, pop: undefined as number | undefined, emp: undefined as number | undefined, presSum: 0, presN: 0 };
      cur.value = measureVal; // per-cell constant across the rows
      if (r.home_share != null) cur.home_share = Number(r.home_share);
      if (r.population != null) cur.pop = Number(r.population);
      if (r.employment != null) cur.emp = Number(r.employment);
      cur.presSum += Number(r.sum_unique_people) || 0; // accumulate presence for the per-cell share
      cur.presN += 1;
      cell.set(k, cur);
    }
    const areas = Array.from(cell.entries()).sort((a, b) => b[1].value - a[1].value).slice(0, 15);
    const total = Array.from(cell.values()).reduce((s, v) => s + v.value, 0);
    const hs = Array.from(cell.values()).map((v) => v.home_share).filter((x): x is number => x != null);
    const avgHomeShare = hs.length ? hs.reduce((a, b) => a + b, 0) / hs.length : 0;
    const values = Array.from(cell.values()).map((v) => v.value);
    const minV = values.length ? Math.min(...values) : 0;
    const maxV = values.length ? Math.max(...values) : 1;
    const valueByCell = new Map(Array.from(cell.entries()).map(([k, v]) => [k, v.value]));

    // ── Placement inputs: census residents/jobs → each cell's share of people ─
    const placementRaw = Array.from(cell.entries()).map(([k, v]) => ({
      name: (v.name || k).replace("1km", ""),
      residents: v.pop ?? 0,
      jobs: v.emp ?? 0,
      avgPresence: v.presN ? v.presSum / v.presN : 0,
    }));
    const totalPres = placementRaw.reduce((s, p) => s + p.avgPresence, 0) || 1;
    const placement = placementRaw
      .map((p) => ({ ...p, sharePct: Math.round((p.avgPresence / totalPres) * 100) }))
      .sort((a, b) => b.avgPresence - a.avgPresence);
    const hasCensus = placement.some((p) => p.residents > 0 || p.jobs > 0);

    // ── Rhythm: people staying over time ─────────────────────────────────────
    const bucketsAreDates = results.some((r) => isDateStr(String(bucket(r.time_bucket))));
    let rhythmLabels: string[] = [];
    let rhythmData: number[] = [];
    let peakLabel = "—";

    if (bucketsAreDates) {
      // daily granularity: one point per calendar day (sum across cells)
      const m = new Map<string, number>();
      for (const r of results) {
        const d = String(bucket(r.time_bucket));
        m.set(d, (m.get(d) || 0) + (Number(r.sum_unique_people) || 0));
      }
      const days = Array.from(m.keys()).sort();
      rhythmLabels = days.map(fmtDate);
      rhythmData = days.map((d) => Math.round(m.get(d) || 0));
      if (days.length) peakLabel = fmtDate(days.reduce((a, b) => ((m.get(b) || 0) > (m.get(a) || 0) ? b : a)));
    } else {
      // hourly: rows carry date + hour → build the full (date,hour) series
      const series = new Map<string, number>(); // "date|hour" → sum across cells
      for (const r of results) {
        const h = String(bucket(r.time_bucket));
        const d = String(r.date ?? "");
        const key = `${d}|${h}`;
        series.set(key, (series.get(key) || 0) + (Number(r.sum_unique_people) || 0));
      }
      const keys = Array.from(series.keys()).sort((a, b) => {
        const [da, ha] = a.split("|");
        const [db, hb] = b.split("|");
        return da === db ? Number(ha) - Number(hb) : da.localeCompare(db);
      });
      if (viewMode === "series") {
        rhythmLabels = keys.map((k) => {
          const [d, h] = k.split("|");
          return h === "0" ? fmtDate(d) : ""; // label day boundaries only
        });
        rhythmData = keys.map((k) => Math.round(series.get(k) || 0));
        if (keys.length) {
          const pk = keys.reduce((a, b) => ((series.get(b) || 0) > (series.get(a) || 0) ? b : a));
          const [pd, ph] = pk.split("|");
          const dow = new Date(`${pd}T00:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
          peakLabel = `${dow} ${ph}:00`;
        }
      } else {
        // average day: collapse the series over dates, per hour-of-day
        const byHour = new Map<string, number[]>();
        for (const k of keys) {
          const [, h] = k.split("|");
          const arr = byHour.get(h) || [];
          arr.push(series.get(k) || 0);
          byHour.set(h, arr);
        }
        const mean = (a: number[]) => a.reduce((s, x) => s + x, 0) / (a.length || 1);
        const hours = Array.from(byHour.keys()).sort((a, b) => Number(a) - Number(b));
        rhythmLabels = hours.map((h) => `${h}:00`);
        rhythmData = hours.map((h) => Math.round(mean(byHour.get(h) || [])));
        if (hours.length) peakLabel = `${hours.reduce((a, b) => (mean(byHour.get(b) || []) > mean(byHour.get(a) || []) ? b : a))}:00`;
      }
    }

    return {
      rhythmLabels,
      rhythmData,
      peakLabel,
      bucketsAreDates,
      areaLabels: areas.map(([k, v]) => (v.name || k).replace("1km", "")),
      areaValues: areas.map(([, v]) => Math.round(v.value)),
      areaHomeShare: areas.map(([, v]) => (v.home_share != null ? Math.round(v.home_share * 100) : 0)),
      total,
      avgHomeShare,
      nAreas: cell.size,
      valueByCell,
      minV,
      maxV,
      placement,
      hasCensus,
    };
  }, [results, measure, viewMode]);

  const heatPolygons: AreaPolygon[] = useMemo(() => {
    const { valueByCell, minV, maxV } = stats;
    const span = maxV - minV;
    return selectedGrids.map((g): AreaPolygon => {
      const v = valueByCell.get(String(g.geo_id));
      const t = v == null ? null : span > 0 ? (v - minV) / span : 0.7;
      const color = t == null ? colors.css("series-10", 0.4) : heatColor(colors, t);
      return { wkt: g.geometry, color };
    });
  }, [selectedGrids, stats, colors]);

  if (!results.length) {
    return (
      <div className="min-h-screen">
        <NavBar title="Activity Dashboard" showBackButton={true} />
        <div className="p-10 text-center text-ink-subtle">
          No results to display — run an Activities job, then click <span className="font-medium">View Dashboard</span>.
        </div>
      </div>
    );
  }

  const rhythmTitle = stats.bucketsAreDates
    ? "People staying by day"
    : viewMode === "series"
    ? "People staying — hourly across the window"
    : "People staying — average day";
  const rhythmSubtitle = stats.bucketsAreDates
    ? "Observed dwelling population per day (Tartu, 10–13 Nov 2025)."
    : viewMode === "series"
    ? "Observed dwelling population, hour by hour across the selected days (Tartu, 10–13 Nov 2025)."
    : "Average of the selected days, by hour of day — the typical-day rhythm (Tartu, 10–13 Nov 2025).";

  return (
    <div className="min-h-screen bg-paper-200">
      <NavBar title="Activity Dashboard — Estonia (observed telco)" showBackButton={true} />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Areas" value={stats.nAreas.toLocaleString()} />
          <Stat label={`${measureLabel} (∑ areas)`} value={Math.round(stats.total).toLocaleString()} />
          <Stat label={stats.bucketsAreDates ? "Peak day (staying)" : "Peak hour (staying)"} value={String(stats.peakLabel)} />
          <Stat label="Avg home share" value={`${Math.round(stats.avgHomeShare * 100)}%`} />
        </div>

        <Card title={`${measureLabel} by 1km cell`} subtitle="Heat map of the selected Tartu cells — darker = more. From observed stays.">
          <div style={{ height: 380 }}>
            <RealtimeAreaMap polygons={heatPolygons} fillOpacity={0.6} />
          </div>
        </Card>

        {stats.hasCensus && (
          <Card
            title="Placement inputs — census residents & jobs"
            subtitle="The model spreads people across the grid using census residents (homes, weighted overnight) and jobs (offices, weighted by day). More residents + jobs → a larger share of the people."
          >
            <div className="overflow-x-auto">
              <table className="table table-sm w-full">
                <thead>
                  <tr className="text-xs text-ink-subtle">
                    <th className="text-left">Cell</th>
                    <th className="text-right">🏠 Residents</th>
                    <th className="text-right">🏢 Jobs</th>
                    <th className="text-right">Share of people</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.placement.map((p) => (
                    <tr key={p.name}>
                      <td className="font-mono text-xs text-ink-muted">{p.name}</td>
                      <td className="text-right tabular-nums">{p.residents.toLocaleString()}</td>
                      <td className="text-right tabular-nums">{p.jobs.toLocaleString()}</td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="tabular-nums w-9 text-right">{p.sharePct}%</span>
                          <div className="w-20 bg-paper-200 rounded h-2">
                            <div className="bg-ok h-2 rounded" style={{ width: `${p.sharePct}%` }} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <Card
          title={rhythmTitle}
          subtitle={rhythmSubtitle}
          right={
            stats.bucketsAreDates ? null : (
              <div className="join">
                <button
                  className={`btn btn-xs join-item ${viewMode === "average" ? "btn-primary" : "btn-ghost"}`}
                  onClick={() => setViewMode("average")}
                >
                  Average day
                </button>
                <button
                  className={`btn btn-xs join-item ${viewMode === "series" ? "btn-primary" : "btn-ghost"}`}
                  onClick={() => setViewMode("series")}
                >
                  Time series
                </button>
              </div>
            )
          }
        >
          <div style={{ height: 300 }}>
            <Bar
              data={{ labels: stats.rhythmLabels, datasets: [{ label: "People staying", data: stats.rhythmData, backgroundColor: colors.css("brand-500") }] }}
              options={{ responsive: true, maintainAspectRatio: false, color: colors.chart.tick, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: colors.chart.tick }, grid: { color: colors.chart.grid } }, y: { ticks: { color: colors.chart.tick }, grid: { color: colors.chart.grid } } } }}
            />
          </div>
        </Card>

        <Card
          title={`Top areas — ${measureLabel.toLowerCase()} & home share`}
          subtitle="Unique counts from observed stays (no double-counting). High home share = residential; low = workplace / commercial."
        >
          <div style={{ height: 320 }}>
            <Bar
              data={{
                labels: stats.areaLabels,
                datasets: [
                  { label: measureLabel, data: stats.areaValues, backgroundColor: colors.css("brand-500"), yAxisID: "y" },
                  { label: "Home share %", data: stats.areaHomeShare, backgroundColor: colors.css("series-3"), yAxisID: "y1" },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                color: colors.chart.tick,
                scales: {
                  y: { type: "linear", position: "left", title: { display: true, text: measureLabel } },
                  y1: { type: "linear", position: "right", min: 0, max: 100, grid: { drawOnChartArea: false }, title: { display: true, text: "Home %" } },
                },
              }}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

// A job on an area made on the catalogue 1.0 maps hands over its request, which the catalogue dashboard runs again in
// full; a finished selection result is drawn as it is; the older Tartu data sends its rows.
const ActivityDashboard: React.FC = () => {
  const location = useLocation();
  const state = (location.state as { ee?: unknown; catalogueJob?: unknown } | null) ?? {};
  if (state.catalogueJob) {
    return <CatalogueActivityDashboard state={state as React.ComponentProps<typeof CatalogueActivityDashboard>["state"]} />;
  }
  return state.ee ? <EeActivityDashboard state={state as React.ComponentProps<typeof EeActivityDashboard>["state"]} /> : <TartuActivityDashboard />;
};

export default ActivityDashboard;
